import React, { useState } from 'react';
import { CreditCard as Edit2, Trash2, AlertTriangle, CheckCircle2, MoreVertical } from 'lucide-react';
import { Button } from '../ui/Button';
import { useAgencies } from '../../hooks/useAgencies';
import { AgencyModal } from './AgencyModal';
import { ConfirmationDialog } from './ConfirmationDialog';
import { Agency } from '../../types/agency';

export const AgencyList: React.FC = () => {
  const { agencies, deleteAgency, suspendAgency, reactivateAgency, error } = useAgencies();
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState('');

  const handleDelete = async () => {
    if (selectedAgency) {
      await deleteAgency(selectedAgency.id);
      setShowDeleteDialog(false);
      setSelectedAgency(null);
    }
  };

  const handleSuspend = async () => {
    if (selectedAgency) {
      await suspendAgency(selectedAgency.id, suspensionReason);
      setShowSuspendDialog(false);
      setSelectedAgency(null);
      setSuspensionReason('');
    }
  };

  const handleReactivate = async () => {
    if (selectedAgency) {
      await reactivateAgency(selectedAgency.id);
      setShowReactivateDialog(false);
      setSelectedAgency(null);
    }
  };

  return (
    <>
      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4 dark:bg-red-900/20">
          <div className="text-sm text-red-500 dark:text-red-400">
            {error}
          </div>
        </div>
      )}

      <div className="mt-8 overflow-hidden rounded-lg border border-gray-200 bg-white shadow dark:border-gray-700 dark:bg-gray-800">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Agence
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Contact
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Statut
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Abonnement
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
            {agencies.map((agency) => (
              <tr key={agency.id}>
                <td className="whitespace-nowrap px-6 py-4">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {agency.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {agency.address}
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4">
                  <div className="text-sm text-gray-900 dark:text-white">
                    {agency.phone}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {agency.email}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4">
                  <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                    agency.status === 'active'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                  }`}>
                    {agency.status === 'active' ? 'Actif' : 'Suspendu'}
                  </span>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {agency.subscription.plan}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSelectedAgency(agency);
                        setShowEditModal(true);
                      }}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    {agency.status === 'active' ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setSelectedAgency(agency);
                          setShowSuspendDialog(true);
                        }}
                      >
                        <AlertTriangle className="h-4 w-4 text-yellow-500" />
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setSelectedAgency(agency);
                          setShowReactivateDialog(true);
                        }}
                      >
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSelectedAgency(agency);
                        setShowDeleteDialog(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modales et dialogues */}
      <AgencyModal
        agency={selectedAgency}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedAgency(null);
        }}
      />

      <ConfirmationDialog
        isOpen={showDeleteDialog}
        title="Supprimer l'agence"
        message="Êtes-vous sûr de vouloir supprimer définitivement cette agence ? Cette action est irréversible."
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={handleDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setSelectedAgency(null);
        }}
        isDangerous
      />

      <ConfirmationDialog
        isOpen={showSuspendDialog}
        title="Suspendre l'agence"
        message="Veuillez indiquer la raison de la suspension :"
        confirmLabel="Suspendre"
        cancelLabel="Annuler"
        onConfirm={handleSuspend}
        onCancel={() => {
          setShowSuspendDialog(false);
          setSelectedAgency(null);
          setSuspensionReason('');
        }}
        isDangerous
      >
        <textarea
          value={suspensionReason}
          onChange={(e) => setSuspensionReason(e.target.value)}
          className="mt-4 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          rows={3}
          placeholder="Raison de la suspension..."
        />
      </ConfirmationDialog>

      <ConfirmationDialog
        isOpen={showReactivateDialog}
        title="Réactiver l'agence"
        message="Êtes-vous sûr de vouloir réactiver cette agence ?"
        confirmLabel="Réactiver"
        cancelLabel="Annuler"
        onConfirm={handleReactivate}
        onCancel={() => {
          setShowReactivateDialog(false);
          setSelectedAgency(null);
        }}
      />
    </>
  );
};